import { useEffect, useRef, useState } from 'react'
import mapboxgl from 'mapbox-gl'
import type { Map as MapboxMap } from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import { config } from '@/config'

export interface UseMapbox {
  containerRef: React.RefObject<HTMLDivElement | null>
  map: MapboxMap | null
  /** True once the style has loaded and sources/layers can be added. */
  ready: boolean
  /** Set when Mapbox can't initialize (bad/restricted token, style/auth failure). */
  error: string | null
}

export interface MapboxOptions {
  center: [number, number]
  zoom: number
  /** Style override (defaults to the app's dark style). */
  style?: string
  /** Add the zoom NavigationControl (default true — off for the compact Storm hero). */
  navControl?: boolean
  /** Allow pan/zoom gestures (default true). */
  interactive?: boolean
}

/** Mapbox error events carry an Error with an optional HTTP status. */
interface MapboxErrorEvent {
  error?: { message?: string; status?: number }
}

/**
 * Owns the Mapbox GL map instance for the weather-map screen: dark base style (M1),
 * phone-tuned gestures, a clean teardown, and FAILURE HANDLING — a malformed token throws
 * from the constructor; an invalid/restricted token fails the style request (401/403) and
 * fires 'error' before 'load'. Either way we surface a message + log the specific error,
 * so the screen shows "Map unavailable" instead of a blank canvas.
 */
export function useMapbox(initial: MapboxOptions): UseMapbox {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const initialRef = useRef(initial)
  const readyRef = useRef(false)
  const [map, setMap] = useState<MapboxMap | null>(null)
  const [ready, setReady] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!containerRef.current || map || !config.map.mapboxToken) return
    mapboxgl.accessToken = config.map.mapboxToken

    const opts = initialRef.current
    let instance: MapboxMap
    try {
      instance = new mapboxgl.Map({
        container: containerRef.current,
        style: opts.style ?? config.map.style,
        center: opts.center,
        zoom: opts.zoom,
        interactive: opts.interactive ?? true,
        pitchWithRotate: false,
        dragRotate: false,
        attributionControl: true,
      })
    } catch (e) {
      // Synchronous failure (e.g. a malformed access token).
      console.error('[weather-map] Mapbox failed to initialize:', e)
      setError(e instanceof Error ? e.message : 'Mapbox failed to initialize.')
      return
    }

    if (opts.navControl ?? true) {
      instance.addControl(new mapboxgl.NavigationControl({ showCompass: false, visualizePitch: false }), 'bottom-right')
    }

    // Mapbox only auto-tracks WINDOW resizes; a container-only size change (portal mount,
    // orientation, layout shift) needs a manual resize or the canvas stays its init size.
    let ro: ResizeObserver | undefined
    if (typeof ResizeObserver !== 'undefined' && containerRef.current) {
      ro = new ResizeObserver(() => instance.resize())
      ro.observe(containerRef.current)
    }

    instance.on('load', () => {
      readyRef.current = true
      setReady(true)
    })

    // Auth/style failures fire 'error'. Treat a failure BEFORE load (or any 401/403) as
    // fatal — the map can't render. After load, log transient tile errors but don't blank.
    instance.on('error', (ev: MapboxErrorEvent) => {
      const err = ev?.error
      console.error('[weather-map] Mapbox error:', err ?? ev)
      const status = err?.status
      if (!readyRef.current || status === 401 || status === 403) {
        setError(err?.message || 'Mapbox failed to load — check the access token.')
      }
    })

    setMap(instance)
    return () => {
      ro?.disconnect()
      instance.remove()
      readyRef.current = false
      setMap(null)
      setReady(false)
    }
    // Create exactly once; container + token are stable for the screen's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { containerRef, map, ready, error }
}
